import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { sharedStyles as styles } from '../../styles/alfredStyles';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import saveThreadToFirestore from '../../utils/saveThreadToFirestore';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';

const flickerStyle = {
  animation: 'flicker 0.8s ease-in-out',
};

const PostCallForm = ({
  existingFormData = {},
  existingChatHistory = [],
  existingSummaryName = '',
  existingThreadId = null,
  postCallResultFull = '',
  isReadOnly = false,
  isThread = false,
  scrollToOutputOnMount = true // ✅ new default
}) => {
  const [inputs, setInputs] = useState({
    meetingOutcome: existingFormData.meetingOutcome || '',
    customOutcome: existingFormData.customOutcome || '',
    contactName: existingFormData.contactName || '',
    contactRole: existingFormData.contactRole || '',
    companyName: existingFormData.companyName || '',
    industry: existingFormData.industry || '',
    geography: existingFormData.geography || '',
    meetingNotes: existingFormData.meetingNotes || '',
    bankerActions: existingFormData.bankerActions || '',
    clientActions: existingFormData.clientActions || ''
  });

  const [result, setResult] = useState(postCallResultFull);
  const [summaryName, setSummaryName] = useState(existingSummaryName);
  const [dots, setDots] = useState('');
  const [loading, setLoading] = useState(false);
  const [askText, setAskText] = useState('');
  const [chatHistory, setChatHistory] = useState(existingChatHistory);
  const [loadingReply, setLoadingReply] = useState(false);
  const chatEndRef = useRef(null);
  const resultRef = useRef(null);
  const [isEditingResult, setIsEditingResult] = useState(false);
  const [editedResultText, setEditedResultText] = useState('');
  const [justSavedResult, setJustSavedResult] = useState('');
  const [justCopied, setJustCopied] = useState(false);
  const [threadId, setThreadId] = useState(existingThreadId || uuidv4());
  const user = getAuth().currentUser;
  const scrollRef = useRef(null);
  const hasScrolled = useRef(false);

  const parseResultToSections = (markdown) => {
    const sections = {};
    let currentSection = '';
    let buffer = [];

    markdown.split('\n').forEach(line => {
      const headerMatch = line.match(/^#+\s(.+)/); // ✅ catches ##, ###, #### etc.

      if (headerMatch) {
        if (currentSection) {
          sections[currentSection] = buffer.join('\n').trim();
        }
        currentSection = headerMatch[1].trim();
        buffer = [];
      } else {
        buffer.push(line);
      }
    });

    if (currentSection) {
      sections[currentSection] = buffer.join('\n').trim();
    }

    return sections;
  };

  useEffect(() => {
    if (!isThread && !hasScrolled.current && chatHistory.length > 0 && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
      hasScrolled.current = true;
    }
  }, [chatHistory, isThread]);

  useEffect(() => {
    if (scrollToOutputOnMount && !hasScrolled.current && result && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      hasScrolled.current = true;
    }
  }, [result, scrollToOutputOnMount]);

  useEffect(() => {
    let interval;
    if (loadingReply) {
      interval = setInterval(() => {
        setDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loadingReply]);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
  };

  useEffect(() => {
    const saveFullThread = async () => {
      if (!result || !threadId) return;

      const fullConversation = [
        { sender: 'alfred', text: result },
        ...chatHistory
      ];

      const combinedText = fullConversation.map(m => `${m.sender === 'user' ? 'You' : 'Alfred'}: ${m.text}`).join('\n\n');

      await saveThreadToFirestore(user?.uid, {
        id: threadId,
        type: 'postcall',
        module: 'Post-Call Follow-Up',
        name:
          summaryName ||
          `Meeting Follow-Up: ${inputs.companyName || 'Company'} – ${inputs.industry || 'Industry'} – ${inputs.contactRole || 'Role'} – ${inputs.geography || 'Region'} – Outcome: ${inputs.customOutcome || inputs.meetingOutcome || 'Unspecified'}`,
        metadata: inputs,
        data: {
          chat: chatHistory,
          postCallResultFull: result
        }
      });
    };

    saveFullThread();
  }, [chatHistory, threadId, result]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputs.companyName.trim() || !inputs.meetingNotes.trim()) {
      alert("Company Name and Meeting Notes are required.");
      return;
    }
    setLoading(true);
    setResult('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/post-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Follow-up generation failed:', errorText);
        setResult("Something went wrong generating Alfred's response.");
        return;
      }

      const data = await response.json();
      hasScrolled.current = false;
      setResult(data.result);
      setThreadId(threadId);

      console.log('📦 Thread About To Save:', {
        id: threadId,
        module: 'Post-Call Follow-Up',
        name:
          summaryName ||
          `Meeting Follow-Up: ${inputs.companyName || 'Company'} – ${inputs.industry || 'Industry'} – ${inputs.contactRole || 'Role'} – ${inputs.geography || 'Region'} – Outcome: ${inputs.customOutcome || inputs.meetingOutcome || 'Unspecified'}`,
        metadata: inputs,
        data: {
          chat: [],
          postCallResultFull: data.result
        }
      });
    } catch (error) {
      console.error('❌ Follow-up generation failed:', error);
      setResult("Something went wrong generating Alfred's response.");
    } finally {
      setLoading(false);
    }
  };

  const handleAskAlfred = async () => {
    if (!askText.trim()) return;

    const updatedChat = [...chatHistory, { sender: 'user', text: askText }];
    setChatHistory(updatedChat);
    setAskText('');
    setLoadingReply(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/post-call/ask-alfred`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageHistory: [
            ...chatHistory.map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text
            })),
            { role: 'user', content: askText }
          ],
          context: {
            meetingOutcome: inputs.meetingOutcome,
            customOutcome: inputs.customOutcome,
            contactName: inputs.contactName,
            contactRole: inputs.contactRole,
            companyName: inputs.companyName,
            companyWebsite: '', // ⬅️ Include this to prevent backend crash
            industry: inputs.industry,
            geography: inputs.geography,
            meetingNotes: inputs.meetingNotes,
            bankerActions: inputs.bankerActions,
            clientActions: inputs.clientActions
          }
        })
      });

      const data = await response.json();
      const reply = data.result || 'Something went wrong.';
      setChatHistory(prev => [...prev, { sender: 'alfred', text: reply }]);
      const updatedConversation = [
        { sender: 'alfred', text: result },
      ];

      const combinedContent = updatedConversation
        .map(msg => `${msg.sender === 'user' ? 'You' : 'Alfred'}: ${msg.text}`)
        .join('\n\n');

      if (threadId) {
        await saveThreadToFirestore(user?.uid, {
          id: threadId,
          type: 'postcall',
          module: 'Post-Call Follow-Up',
          name:
            summaryName ||
            `Meeting Follow-Up: ${inputs.companyName || 'Company'} – ${inputs.industry || 'Industry'} – ${inputs.contactRole || 'Role'} – ${inputs.geography || 'Region'} – Outcome: ${inputs.customOutcome || inputs.meetingOutcome || 'Unspecified'}`,
          metadata: inputs,
          data: {
            chat: updatedConversation,
            postCallResultFull: isEditingResult ? editedResultText : result
          }
        });
      }
    } catch (err) {
      console.error('❌ Ask Alfred failed:', err);
      setChatHistory(prev => [...prev, { sender: 'alfred', text: 'Something went wrong.' }]);
    } finally {
      setLoadingReply(false);
    }
  };

  const handleExportToWord = () => {
    if (!result) return;

    const followUpData = parseResultToSections(result || '');
    const paragraphs = [
      new Paragraph({
        children: [new TextRun({ text: 'Meeting Follow-Up Summary', bold: true, size: 36 })],
        spacing: { after: 400 },
      }),
    ];

    // ✅ If no headers/sections were found, just export the whole thing as one block
    if (Object.keys(followUpData).length === 0) {
      result.split('\n').forEach((line) => {
        if (line.trim() !== '') {
          paragraphs.push(
            new Paragraph({
              children: [new TextRun({ text: line.trim(), size: 24 })],
              spacing: { after: 100 },
            })
          );
        }
      });
    } else {
      Object.entries(followUpData).forEach(([section, text]) => {
        paragraphs.push(
          new Paragraph({
            children: [new TextRun({ text: section, bold: true, size: 28, underline: {} })],
            spacing: { after: 200 },
          })
        );

        text.split('\n').forEach((line) => {
          if (line.trim() !== '') {
            paragraphs.push(
              new Paragraph({
                children: [new TextRun({ text: line.trim(), size: 24 })],
                spacing: { after: 100 },
              })
            );
          }
        });
      });
    }

    const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });

    Packer.toBlob(doc).then((blob) => {
      const cleanDefault = (inputs.companyName || 'client').replace(/[^\w\s-]/g, '');
      const cleanCustom = (summaryName || '').replace(/[^\w\s-]/g, '');
      const finalName = cleanCustom || `alfred-follow-up-${cleanDefault}`;
      saveAs(blob, `${finalName}.docx`);
    });
  };

  useEffect(() => {
    const saveOnRename = async () => {
      if (!summaryName || !threadId || !result) return;

      await saveThreadToFirestore(user?.uid, {
        id: threadId,
        type: 'postcall',
        module: 'Post-Call Follow-Up',
        name: summaryName,
        metadata: inputs,
        data: {
          chat: chatHistory,
          postCallResultFull: result
        }
      });
    };

    saveOnRename();
  }, [summaryName]);

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          marginBottom: '30px',
          borderBottom: '4px solid #FFD700',
          display: 'inline-block',
          paddingBottom: '10px'
        }}>
          Execute Post-Meeting Follow-Up
        </h2>

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div style={{ width: '100%', maxWidth: '927px' }}>
            
            <div style={{ width: '107.4%' }}>
              <label style={styles.label}>Meeting Outcome</label>
              <select
                style={styles.input}
                name="meetingOutcome"
                value={inputs.meetingOutcome}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              >
                <option value="">-- Select Outcome --</option>
                <option>Needs time to decide</option>
                <option>Requested pricing</option>
                <option>Not a fit</option>
                <option>Scheduling follow-up</option>
                <option>Sent to decision-maker</option>
              </select>
            </div>

          <div style={{ width: '103.6%' }}>
            <div>
              <label style={styles.label}>Custom Outcome (optional)</label>
              <input
                style={styles.input}
                name="customOutcome"
                placeholder="e.g., Ready to move forward..."
                value={inputs.customOutcome}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              />
            </div>

            <div>
              <label style={styles.label}>Contact Name</label>
              <input
                style={styles.input}
                name="contactName"
                placeholder="e.g,. Alfred"
                value={inputs.contactName}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              />
            </div>

            <div>
              <label style={styles.label}>Contact Role</label>
              <input
                style={styles.input}
                name="contactRole"
                placeholder="e.g., CEO"
                value={inputs.contactRole}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              />
            </div>

            <div>
              <label style={styles.label}>Company Name</label>
              <input
                style={styles.input}
                name="companyName"
                placeholder="e.g., Alfred's Roofing"
                value={inputs.companyName}
                onChange={handleChange} required
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              />
            </div>

            <div>
              <label style={styles.label}>Industry</label>
              <input
                style={styles.input}
                name="industry"
                placeholder="e.g., Roofing"
                value={inputs.industry}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              />
            </div>

            <div>
              <label style={styles.label}>Geography</label>
              <input
                style={styles.input}
                name="geography"
                placeholder="e.g., Central Florida"
                value={inputs.geography}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
              />
            </div>

            <div>
              <label style={styles.label}>Key Meeting Notes</label>
              <textarea
                style={{ ...styles.input, fontFamily: "'Helvetica Neue', sans-serif" }}
                name="meetingNotes"
                placeholder="e.g., Write out the key points from the meeting."
                value={inputs.meetingNotes}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
                rows={4}
              />
            </div>

            <div>
              <label style={styles.label}>Action Items for Banker</label>
              <textarea
                style={{ ...styles.input, fontFamily: "'Helvetica Neue', sans-serif" }}
                name="bankerActions"
                placeholder="e.g., Write out all of the takeaway actions you have from the meeting."
                value={inputs.bankerActions}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
                rows={3}
              />
            </div>

            <div>
              <label style={styles.label}>Action Items for Prospect / Client</label>
              <textarea
                style={{ ...styles.input, fontFamily: "'Helvetica Neue', sans-serif" }}
                name="clientActions"
                placeholder="e.g., Write out all of the tasks you and the Prospect / Client agreed they would complete, or that you need from them to be able to complete your actions."
                value={inputs.clientActions}
                onChange={handleChange}
                disabled={isReadOnly}
                className={isReadOnly ? styles.readonlyInput : ''}
                rows={3}
              />
            </div>
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? `Alfred is Generating Your Follow Up Plan${dots} ✏️` : 'Generate Follow-Up Plan'}
          </button>
          </div>
        </form>

        {result && (
          <>
            <div ref={scrollRef} style={{ position: 'relative', top: '-80px' }} />
            <div style={styles.resultBox}>
              <h2 style={{ marginBottom: '20px' }}>📋 Follow-Up Briefing</h2>
              <div style={{ lineHeight: '1.7', fontSize: '16px', fontFamily: `'Helvetica Neue', sans-serif`, paddingBottom: '24px' }}>
                {isEditingResult ? (
                  <>
                    <textarea
                      value={editedResultText}
                      onChange={(e) => setEditedResultText(e.target.value)}
                      rows={16}
                      style={{
                        width: '95%',
                        padding: '16px',
                        fontSize: '16px',
                        fontFamily: `'Helvetica Neue', sans-serif`,
                        lineHeight: '1.6',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        border: '1px solid #ccc',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
                        whiteSpace: 'pre-wrap'
                      }}
                    />
                    <button
                      onClick={async () => {
                        setResult(editedResultText);
                        setIsEditingResult(false);
                        setJustSavedResult('yes');
                        setTimeout(() => setJustSavedResult(''), 800);

                        try {
                          await saveThreadToFirestore(user?.uid, {
                            id: threadId,
                            type: 'postcall',
                            module: 'Post-Call Follow-Up',
                            name:
                              summaryName ||
                              `Meeting Follow-Up: ${inputs.companyName || 'Company'} – ${inputs.industry || 'Industry'} – ${inputs.contactRole || 'Role'} – ${inputs.geography || 'Region'} – Outcome: ${inputs.customOutcome || inputs.meetingOutcome || 'Unspecified'}`,
                            metadata: inputs,
                            data: {
                              chat: chatHistory,
                              postCallResultFull: editedResultText
                            }
                          });
                          console.log('💾 Saved edited follow-up result');
                        } catch (err) {
                          console.error('❌ Failed to save edited follow-up:', err);
                        }
                      }}
                      style={{ marginTop: '8px', ...styles.button }}
                    >
                      💾 Save
                    </button>
                  </>
                ) : (
                  <div style={justSavedResult ? flickerStyle : {}}>
                    <ReactMarkdown
                      components={{
                        h2: ({node, ...props}) => <h2 style={{ marginTop: '24px', fontSize: '20px', fontWeight: 'bold' }} {...props} />,
                        h3: ({node, ...props}) => <h3 style={{ marginTop: '20px', fontSize: '18px', fontWeight: '600' }} {...props} />,
                        p: ({node, ...props}) => <p style={{ marginBottom: '14px' }} {...props} />,
                        li: ({node, ...props}) => <li style={{ marginBottom: '6px' }} {...props} />,
                        ul: ({node, ...props}) => <ul style={{ paddingLeft: '20px', marginBottom: '12px' }} {...props} />,
                        ol: ({node, ...props}) => <ol style={{ paddingLeft: '20px', marginBottom: '12px' }} {...props} />,
                      }}
                    >
                      {result}
                    </ReactMarkdown>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                      <button
                        onClick={() => {
                          setIsEditingResult(true);
                          setEditedResultText(result);
                        }}
                        style={{
                          backgroundColor: '#f6f6f6',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '10px 16px',
                          fontWeight: '600',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <span role="img" aria-label="edit">📝</span> Edit
                      </button>

                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(result);
                          setJustCopied(true);
                          setTimeout(() => setJustCopied(false), 1000);
                        }}
                        style={{
                          backgroundColor: '#f6f6f6',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '10px 16px',
                          fontWeight: '600',
                          fontSize: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          ...(justCopied ? flickerStyle : {}),
                        }}
                      >
                        {justCopied ? '✅ Copied!' : '📋 Copy'}
                      </button>
                    </div>
                  </div>
                )}
                
                <div style={{ marginTop: '20px' }}>
                  <label style={styles.label}>🏷️ Optional Follow-Up Summary Name</label>
                  <input
                    type="text"
                    placeholder="e.g., March Discovery Recap"
                    value={summaryName}
                    onChange={(e) => setSummaryName(e.target.value)}
                    style={{
                      width: '96.5%',
                      padding: '12px',
                      fontSize: '16px',
                      borderRadius: '10px',
                      border: '1px solid #ccc',
                      marginBottom: '0px'
                    }}
                  />
                </div>

                <button
                  onClick={handleExportToWord}
                  style={{ ...styles.button, marginTop: '12px', marginBottom: '20px' }}
                >
                  Export Follow-Up to Word
                </button>
              </div>

              <div style={styles.chatContainer}>
                {chatHistory.map((msg, idx) => (
                  <div key={idx} style={{
                    ...styles.chatBubble,
                    ...(msg.sender === 'user' ? styles.userBubble : styles.alfredBubble)
                  }}>
                    <strong>{msg.sender === 'user' ? 'You' : 'Alfred'}:</strong><br />
                    {msg.text}
                  </div>
                ))}

                {loadingReply && (
                  <div style={{
                    ...styles.chatBubble,
                    ...styles.alfredBubble,
                    fontStyle: 'italic',
                    marginBottom: '12px'
                  }}>
                    <strong>Alfred:</strong><br />
                    Alfred is typing{dots}
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              <div style={{ width: '100%', marginTop: '30px' }}>
                <label style={{ ...styles.label, marginBottom: '8px' }}>Ask Alfred a Follow-Up Question:</label>
    
                <textarea
                  style={{
                    ...styles.input,
                    width: '96%',
                    fontFamily: "'Helvetica Neue', sans-serif"
                  }}
                  value={askText}
                  placeholder="e.g., What can I do to accelerate this opportunity?"
                  onChange={(e) => setAskText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAskAlfred();
                    }
                  }}
                />

                <button
                  style={styles.button}
                  onClick={handleAskAlfred}
                  disabled={loadingReply}
                >
                  Ask Alfred
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PostCallForm;